/**
 * The stage: the film at its own proportions, never cropped. The box takes the clip's aspect ratio
 * from the wire's width and height before the first frame arrives, so nothing moves when it does.
 * In compare mode a second film of the same movement plays beside it on one clock, shifted by an
 * offset the trainer sets by hand for this session only. A film that will not load (its signed url
 * expired, the file is refused) says so inside the same box, and a second go reads the clip again.
 */

import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { clipAspect, type ClipEntry } from '~/entities/exercise-clip';
import { numberFormat } from '~/shared/lib/intlCache';
import ghost from '~/shared/ui/ghostButton.module.css';
import { ChevronGlyph, LinkGlyph, ZoomGlyph } from '~/shared/ui/VideoGlyphs';

import type { ClipPlayback } from '../lib/useClipPlayback';
import type { ZoomOrigin } from '../model/playback';

import styles from './ClipPlayer.module.css';

/** How far the partner may drift from the main film's clock before it is pulled back. */
const DRIFT_SECONDS = 0.08;

interface PlayerStageProps {
  readonly clip: ClipEntry;
  /** The main film: the stage attaches it, and the compared one follows its clock. */
  readonly playback: ClipPlayback;
  readonly zoomed: boolean;
  /** Where the zoom is anchored; the pointer and the arrows both move it. */
  readonly origin: ZoomOrigin;
  readonly onOrigin: (origin: ZoomOrigin) => void;
  readonly compareWith: ClipEntry | null;
  readonly labelOf: (clip: ClipEntry) => string;
  readonly videoLabel: string;
  /** Read the clip again, so its film has a freshly signed url. */
  readonly onReloadFilm: (clipId: number) => Promise<void>;
}

/** The main film of one clip. `attempt` is the element's key: a second go is a fresh element. */
interface FilmState {
  readonly clipId: number;
  readonly attempt: number;
  readonly status: 'loading' | 'failed' | 'retrying';
}

const ratioOf = (clip: ClipEntry) => ({ '--clip-ratio': String(clipAspect(clip.media)) }) as CSSProperties;

export function PlayerStage({
  clip,
  playback,
  zoomed,
  origin,
  onOrigin,
  compareWith,
  labelOf,
  videoLabel,
  onReloadFilm,
}: PlayerStageProps) {
  const { t, i18n } = useTranslation();
  const partnerRef = useRef<HTMLVideoElement | null>(null);
  const [offset, setOffset] = useState(0);
  const [linked, setLinked] = useState(true);
  const [film, setFilm] = useState<FilmState>({ clipId: clip.id, attempt: 0, status: 'loading' });
  if (film.clipId !== clip.id) setFilm({ clipId: clip.id, attempt: 0, status: 'loading' });
  const filmFailed = film.clipId === clip.id && film.status !== 'loading';
  const comparing = compareWith !== null;
  const partnerId = compareWith?.id ?? null;
  const { attach, clock, playing, rate } = playback;
  const seconds = numberFormat(i18n.language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  });

  // The partner takes the main film's speed and play state as they change, and its time on the
  // frames the main clock is fed — so nothing runs per frame while both stand still.
  useEffect(() => {
    const other = partnerRef.current;
    if (clock === null || other === null || !linked) return undefined;
    if (other.playbackRate !== rate) other.playbackRate = rate;
    if (playing && other.paused) void other.play().catch(() => undefined);
    if (!playing && !other.paused) other.pause();
    const follow = () => {
      const target = clock.now() + offset;
      if (Math.abs(other.currentTime - target) > DRIFT_SECONDS) other.currentTime = Math.max(0, target);
    };
    follow();
    return clock.subscribe(follow);
  }, [clock, playing, rate, offset, linked, partnerId]);

  const settleFilm = (status: FilmState['status']) => {
    setFilm((current) => (current.clipId === clip.id ? { ...current, status } : current));
  };

  const retryFilm = () => {
    if (film.status !== 'failed') return;
    const id = clip.id;
    settleFilm('retrying');
    void onReloadFilm(id).then(() => {
      setFilm((current) => (current.clipId === id ? { ...current, attempt: current.attempt + 1 } : current));
    });
  };

  const pan = (event: PointerEvent<HTMLVideoElement>) => {
    if (!zoomed || event.buttons !== 1) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = Math.round(((event.clientX - bounds.left) / bounds.width) * 100);
    const y = Math.round(((event.clientY - bounds.top) / bounds.height) * 100);
    if (x !== origin.x || y !== origin.y) onOrigin({ x, y });
  };

  return (
    <>
      <div className={styles.stageArea}>
        <div
          className={`${String(styles.stageBox)} ${comparing ? String(styles.stageBoxHalf) : ''}`}
          style={ratioOf(clip)}
        >
          <video
            aria-label={videoLabel}
            autoPlay
            className={`${String(styles.video)} ${zoomed ? String(styles.videoZoomed) : ''}`}
            key={`${String(clip.id)}:${String(film.attempt)}`}
            loop
            muted
            onError={() => {
              settleFilm('failed');
            }}
            onLoadedData={() => {
              settleFilm('loading');
            }}
            onPointerDown={pan}
            onPointerMove={pan}
            playsInline
            poster={clip.media.posterUrl ?? undefined}
            preload="auto"
            ref={attach}
            src={clip.media.videoUrl ?? undefined}
            style={{ '--zoom-origin': `${String(origin.x)}% ${String(origin.y)}%` } as CSSProperties}
          />
          {filmFailed ? (
            <div className={styles.filmNotice}>
              <span role="alert">{t('web:clipPlayer.states.filmFailed')}</span>
              <button
                aria-busy={film.status === 'retrying'}
                className={`${String(ghost.ghost)} ${String(styles.small)} ${String(styles.filmRetry)}`}
                onClick={retryFilm}
                type="button"
              >
                {t('web:clipPlayer.states.retry')}
              </button>
            </div>
          ) : null}
          {comparing || zoomed ? (
            <span className={styles.stageLabels}>
              {comparing ? <span className={styles.stageLabel}>{labelOf(clip)}</span> : null}
              {zoomed ? (
                <span className={styles.stageLabel}>
                  <ZoomGlyph className={styles.glyphSmall} />
                  {t('web:clipPlayer.controls.zoomHint', {
                    factor: numberFormat(i18n.language, {}).format(1.9),
                  })}
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
        {compareWith === null ? null : (
          <div className={`${String(styles.stageBox)} ${String(styles.stageBoxHalf)}`} style={ratioOf(compareWith)}>
            <video
              aria-label={labelOf(compareWith)}
              className={styles.video}
              key={compareWith.id}
              loop
              muted
              playsInline
              poster={compareWith.media.posterUrl ?? undefined}
              preload="auto"
              ref={partnerRef}
              src={compareWith.media.videoUrl ?? undefined}
            />
            <span className={styles.stageLabels}>
              <span className={styles.stageLabel}>{labelOf(compareWith)}</span>
            </span>
          </div>
        )}
      </div>
      {compareWith === null ? null : (
        <div className={styles.compareRow}>
          <span>{t('web:clipPlayer.compare.align')}</span>
          <button
            aria-label={t('web:clipPlayer.compare.nudgeBack')}
            className={styles.icon}
            onClick={() => {
              setOffset((current) => current - playback.frameSeconds());
            }}
            type="button"
          >
            <ChevronGlyph className={styles.glyph} direction="left" />
          </button>
          <span className={styles.compareOffset}>
            {t('web:clipPlayer.compare.offset', { value: seconds.format(offset) })}
          </span>
          <button
            aria-label={t('web:clipPlayer.compare.nudgeForward')}
            className={styles.icon}
            onClick={() => {
              setOffset((current) => current + playback.frameSeconds());
            }}
            type="button"
          >
            <ChevronGlyph className={styles.glyph} direction="right" />
          </button>
          <button
            aria-pressed={linked}
            className={`${String(styles.chip)} ${linked ? String(styles.chipOn) : ''}`}
            onClick={() => {
              setLinked((current) => !current);
            }}
            type="button"
          >
            <LinkGlyph className={styles.glyphSmall} />
            {t(linked ? 'web:clipPlayer.compare.linked' : 'web:clipPlayer.compare.separate')}
          </button>
        </div>
      )}
    </>
  );
}
